import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Button, ModalBody } from "reactstrap"
import { get, post } from "helpers/api_helper"
import DraftModal from "./DraftModal"
import ParcelSelector, { documentsOnly, initialSelection, parcelWeights, selectionBody, selectionCheck } from "./ParcelSelector"
import { blockerText, shipmentName } from "./shipmentLabels"
import { LOGISTICS_MESSAGES, logisticsReadMessage } from "./useLogisticsOperation"

const PROMPT_FAILED = "The prompt could not be generated. Try again."

// „Generate AI prompt” (US 97, ticket 20 Q13, Q14): for a shipment in preparation with a Romanian address, the Romanian text an AI agent
// (e.g. Claude Code) is given to create the AWB in Cargus WebExpress, in the browser session where the administrator is signed in. The
// administrator chooses its parcels, as for Cargus's generation, and copies it; nothing is saved or logged. The AWB it brings back is entered
// at „Validate sheet” or „Validate processing”, a supplementary shipment's at its processing. What blocks a courier is shown instead of the
// prompt. A supplementary shipment (`shipment`) takes its own. Parcels typed are kept until they are discarded (DraftModal).
export default function AwbPromptDialog({ eventId, sheet, shipment = null, onValidate, onClose }) {
  const [read, setRead] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  const [start, setStart] = useState(null)
  const [selection, setSelection] = useState(null)
  const [answer, setAnswer] = useState({})
  const [copied, setCopied] = useState(null)
  // The last prompt asked for: an answer to an earlier one, or one arriving after the dialog closed, is dropped.
  const latest = useRef(0)
  useEffect(() => () => { latest.current += 1 }, [])
  const shipmentId = shipment?.id
  useEffect(() => {
    let active = true
    setRead(previous => ({ ...previous, loading: true, error: false }))
    const url = `/admin/logistics/events/${eventId}/shipments/validation`
    Promise.all([shipmentId ? get(url, { params: { shipment: shipmentId } }) : get(url), get("/admin/logistics/settings")]).then(([preview, settings]) => {
      if (!active) return
      const documents = documentsOnly(preview.data.shipment)
      setRead({ preview: preview.data, packages: settings.data.packages, documents })
      const first = initialSelection(settings.data.packages, { weights: parcelWeights(preview.data.shipment), documents })
      setStart(previous => previous || first)
      setSelection(previous => previous || first)
    }).catch(() => { if (active) setRead({ error: true }) })
    return () => { active = false }
  }, [eventId, shipmentId, attempt])

  const { preview, packages = [], documents } = read
  const blockers = preview?.blockers.courier || []
  const ready = preview && !blockers.length && selection && selectionCheck(selection, packages).valid
  const dirty = Boolean(start) && JSON.stringify(selection) !== JSON.stringify(start)
  // A changed selection makes the prompt stale, even one still being generated.
  const choose = next => { latest.current += 1; setSelection(next); setAnswer({}); setCopied(null) }
  const generate = () => {
    latest.current += 1
    const request = latest.current
    const answered = result => { if (latest.current === request) setAnswer(result) }
    setAnswer({ pending: true })
    setCopied(null)
    post(`/admin/logistics/events/${eventId}/shipments/awb-prompt`, { ...(shipmentId ? { shipment: shipmentId } : {}), ...selectionBody(selection) })
      .then(response => answered({ prompt: response.data.prompt }))
      // A code the map knows keeps its text. A 400 without one is the gateway's check of the parcels; anything else (production's masked 500,
      // no answer) is asked again.
      .catch(error => {
        const { status, data } = error?.response || {}
        answered({ failure: logisticsReadMessage(data?.message, status === 400 ? LOGISTICS_MESSAGES.INVALID_LOGISTICS_PARCELS : PROMPT_FAILED) })
      })
  }
  const copy = () => navigator.clipboard.writeText(answer.prompt).then(() => setCopied("done"), () => setCopied("failed"))

  return <DraftModal id="logistics-prompt-title" title={`AI prompt for AWB — ${shipmentName(shipment)}`} size="lg" dirty={dirty} closeLabel="Close"
    onClose={onClose} actions={<>
      {answer.prompt && <Button color="primary" outline onClick={copy}>
        <i className={`bx ${copied === "done" ? "bx-check" : "bx-copy"} mr-1`} aria-hidden="true" />{copied === "done" ? "Copied" : "Copy"}
      </Button>}
      <Button color="primary" onClick={onValidate}>I received the AWB → Validate</Button>
    </>}>
    <ModalBody>
      <p className="small text-muted">Paste it into an AI session (e.g. Claude Code) that can use your browser, where you are signed in to Cargus WebExpress.
        The prompt contains no credentials and does not validate the shipment: enter the AWB at Validate sheet or Validate processing.</p>
      {read.loading && !preview && <p role="status">Reading the shipment and the packages…</p>}
      {read.error && <Alert color="danger">The shipment could not be read. <Button color="link" className="p-0 align-baseline"
        onClick={() => setAttempt(value => value + 1)}>Retry</Button></Alert>}
      {blockers.length > 0 && <Alert color="danger" className="small">
        <p className="mb-1">A courier cannot take this shipment yet:</p>
        <ul className="mb-0">{blockers.map(blocker => <li key={blocker.kind}>{blockerText(blocker, sheet)}</li>)}</ul>
      </Alert>}
      {preview && !blockers.length && (selection?.parcels.length ? <ParcelSelector packages={packages} selection={selection} onChange={choose}
        weights={parcelWeights(preview.shipment)} documents={documents} />
        : <Alert color="warning" className="small">No shipping package is active. Add one in <Link to="/logistics/settings">Logistics → Settings</Link>.</Alert>)}
      {preview && !blockers.length && preview.shipment.content.some(item => item.weight == null) && <p className="small text-warning">
        Some products have no unit weight: enter the parcels' weight after weighing them.</p>}
      <Button color="primary" disabled={!ready || answer.pending} onClick={generate}>
        <i className="bx bx-bot mr-1" aria-hidden="true" />{answer.pending ? "Generating…" : "Generate prompt"}
      </Button>
      {answer.failure && <Alert color="danger" className="mt-3 mb-0">{answer.failure}</Alert>}
      {answer.prompt && <pre className="logistics-prompt mt-3 mb-0" aria-label="AI prompt">{answer.prompt}</pre>}
      {copied === "failed" && <p className="small text-danger mt-2 mb-0">The prompt could not be copied: select its text and copy it.</p>}
    </ModalBody>
  </DraftModal>
}
